import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useDraggable } from '../hooks/useDraggable';

const JobHistoryPiP = ({ jobName, onSelectJob, onClose }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Start positioned in the bottom left area above the FAB
  const { position, handlers: dragHandlers } = useDraggable(() => {
    if (typeof window !== 'undefined') {
      return {
        x: 90, // Next to the FAB
        y: window.innerHeight - (window.innerHeight * 0.6) - 40 // 60vh height + 40px padding
      };
    }
    return { x: 20, y: 20 };
  });

  useEffect(() => {
    const q = query(
      collection(db, 'job_logs'),
      where('jobName', '==', jobName)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Sort locally to avoid needing a composite index
      docs.sort((a, b) => {
        const timeA = a.createdAt?.toMillis() || 0;
        const timeB = b.createdAt?.toMillis() || 0;
        return timeB - timeA;
      });
      setJobs(docs);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching job logs", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [jobName]);

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp.toMillis()).toLocaleString();
  };

  const getStatusColor = (status) => {
    if (status === 'completed') return 'bg-green-900/50 text-green-400 border-green-700';
    if (status === 'failed') return 'bg-red-900/50 text-red-400 border-red-700';
    if (status === 'running') return 'bg-blue-900/50 text-blue-400 border-blue-700 animate-pulse';
    return 'bg-gray-800 text-gray-300 border-gray-600';
  };

  return (
    <div
      className="fixed w-[350px] bg-gray-900 rounded-xl shadow-2xl border border-gray-700 flex flex-col z-[100] overflow-hidden"
      style={{
        height: '60vh',
        left: position.x + 'px',
        top: position.y + 'px',
        margin: 0
      }}
    >
      <div
        className="bg-gray-800 px-4 py-3 flex justify-between items-center border-b border-gray-700 cursor-move select-none"
        {...dragHandlers}
      >
        <h3 className="font-bold font-mono text-sm flex items-center gap-2 pointer-events-none truncate max-w-[250px] text-gray-100">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {jobName} History
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors flex-shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 pointer-events-none" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
      
      <div className="overflow-y-auto flex-1 p-2 bg-black">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-400 border-t-transparent"></div>
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center text-gray-500 p-4 text-sm mt-4 font-mono">
            No history found for this job.
          </div>
        ) : (
          <div className="space-y-2">
            {jobs.map(job => (
              <button 
                key={job.id} 
                onClick={() => onSelectJob(job.jobId)}
                className="w-full text-left p-3 border border-gray-800 bg-gray-900 hover:border-gray-600 hover:bg-gray-800 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent group"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="text-xs font-semibold text-gray-300 group-hover:text-white transition-colors font-mono">
                    {formatTime(job.createdAt)}
                  </div>
                  <div className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${getStatusColor(job.status)}`}>
                    {job.status || 'unknown'}
                  </div>
                </div>
                <div className="text-xs text-gray-500 font-mono truncate group-hover:text-gray-400 transition-colors">
                  ID: {job.jobId?.substring(0, 8)}...
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobHistoryPiP;
